import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2, Trash2, ChevronLeft, ChevronRight, Copy, Lock, Send, Film } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { AccountAvatar, NETS, StatusPill, MediaThumb, uploadSocialMedia, fmtWhen, formatOf, isStory } from "@/lib/social";
import { PostPreview } from "@/components/social/PostPreview";
import { DuePicker } from "@/components/TaskShared";

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);

const LIMITS = { facebook: 63206, instagram: 2200, linkedin: 3000, tiktok: 2200 };

function MediaStrip({ post, locked, onChange }) {
  const [up, setUp] = useState(null);
  const ref = useRef(null);
  const add = async (files) => {
    for (const f of files) {
      if (!f.type.startsWith("image/") && !f.type.startsWith("video/")) { toast.error(`${f.name} isn't an image or video`); continue; }
      try { setUp({ name: f.name, pct: 0 }); await uploadSocialMedia(post.id, f, (pct) => setUp({ name: f.name, pct })); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    setUp(null); onChange();
  };
  const move = async (i, d) => {
    const ids = post.media.map((m) => m.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    await api.put(`/console/social/posts/${post.id}`, { media_order: ids }).catch(() => {}); onChange();
  };
  const remove = async (m) => { await api.delete(`/console/social/posts/${post.id}/media/${m.id}`).catch(() => {}); onChange(); };
  return (
    <div className="space-y-3">
      {post.media?.length > 0 && (
        <div className="flex flex-wrap gap-2.5">
          {post.media.map((m, i) => (
            <div key={m.id} data-testid={`social-media-${m.id}`} className="group relative h-28 w-28 rounded-xl overflow-hidden border border-[#EEEFEF]">
              <MediaThumb m={m} className="h-full w-full" />
              {m.kind === "video" && <Film className="absolute top-1.5 left-1.5 h-4 w-4 text-white drop-shadow" />}
              {!locked && (
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/55 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="text-white disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                  <button data-testid={`social-media-remove-${m.id}`} onClick={() => remove(m)} className="text-white"><Trash2 className="h-3.5 w-3.5" /></button>
                  <button disabled={i === post.media.length - 1} onClick={() => move(i, 1)} className="text-white disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!locked && (
        <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add([...e.dataTransfer.files]); }} onClick={() => ref.current?.click()}
          data-testid="social-media-drop" className="rounded-2xl border border-dashed border-[#d9d9e3] bg-white py-7 text-center cursor-pointer hover:border-[#723FFF] transition-colors">
          {up ? <p className="text-sm text-[#595D62] flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading {up.name} · {up.pct}%</p> : (
            <>
              <Upload className="h-6 w-6 text-[#723FFF] mx-auto" />
              <p className="text-sm text-[#06060C] mt-1.5">Drop or <span className="text-[#723FFF]">upload</span> images or a video</p>
              <p className="text-[11px] text-[#9FA1A4]">Several images make a carousel · JPG, PNG, GIF, WEBP, MP4, MOV</p>
            </>
          )}
          <input ref={ref} type="file" multiple accept="image/*,video/*" className="hidden" data-testid="social-media-input" onChange={(e) => { add([...e.target.files]); e.target.value = ""; }} />
        </div>
      )}
    </div>
  );
}

function PostTypeSwitch({ post, locked, chosen, onChange }) {
  const story = isStory(post);
  const other = chosen.filter((a) => a.network !== "instagram" && a.network !== "facebook");
  return (
    <div>
      <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-2">Post type</p>
      <div className="inline-flex rounded-full border border-[#EEEFEF] bg-white p-0.5" data-testid="social-editor-type">
        {[["feed", "Feed post"], ["story", "Story · 9:16"]].map(([k, l]) => (
          <button key={k} data-testid={`social-editor-type-${k}`} disabled={locked} onClick={() => onChange(k)} className={`rounded-full px-3.5 py-1.5 text-xs transition-colors disabled:opacity-60 ${(story ? "story" : "feed") === k ? "bg-[#06060C] text-white" : "text-[#595D62] hover:text-[#06060C]"}`}>{l}</button>
        ))}
      </div>
      {story && other.length > 0 && <p className="text-[11px] text-amber-700 mt-1.5" data-testid="social-editor-story-note">Stories are for Instagram and Facebook. {other.map((a) => a.name).join(", ")} will show as a regular post.</p>}
    </div>
  );
}

function Comments({ post, onChange }) {
  const [text, setText] = useState("");
  const send = async () => {
    try { await api.post(`/console/social/posts/${post.id}/comments`, { text }); setText(""); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="social-editor-comments">
      <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Feedback & comments</p>
      {(post.comments || []).map((c) => (
        <div key={c.id} className={`rounded-xl px-3 py-2 text-[13px] ${c.by_type === "client" ? "bg-amber-50" : "bg-[#F6F6FD]"}`}>
          <p className="whitespace-pre-wrap">{c.text}</p>
          <p className="text-[10px] text-[#9FA1A4] mt-0.5">{c.by}{c.by_type === "client" ? " (client)" : c.visibility === "internal" ? " · internal" : ""} · {fmtWhen(c.at)}</p>
        </div>
      ))}
      <div className="flex gap-2"><Input data-testid="social-editor-comment-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim() && send()} placeholder="Internal note (clients don't see it)" className="bg-[#F6F6FD] border-[#EEEFEF] h-9" />
        <button data-testid="social-editor-comment-send" onClick={send} disabled={!text.trim()} className="h-9 w-9 shrink-0 rounded-full bg-[#06060C] text-white flex items-center justify-center disabled:opacity-30"><Send className="h-4 w-4" /></button></div>
    </div>
  );
}

export function PostEditor({ post: initial, accounts, ws, editable, onClose, onChanged }) {
  const [post, setPost] = useState(initial);
  const [preview, setPreview] = useState(initial.account_ids?.[0]);
  const saveT = useRef(null);
  const pending = useRef({});
  useEffect(() => setPost(initial), [initial]);
  const flush = () => {
    clearTimeout(saveT.current);
    const body = pending.current; pending.current = {};
    if (!Object.keys(body).length) return Promise.resolve();
    return api.put(`/console/social/posts/${initial.id}`, body).then(() => onChanged(false)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => () => { flush(); }, []); // eslint-disable-line
  const locked = !editable || post.status === "approved" || post.status === "scheduled";
  const patch = (body, now = false) => {
    setPost((p) => ({ ...p, ...body }));
    pending.current = { ...pending.current, ...body };
    clearTimeout(saveT.current);
    saveT.current = setTimeout(flush, now ? 0 : 600);
  };
  const close = () => flush().then(onClose);
  const reload = () => { flush().then(() => onChanged(true)); };
  const toggle = (id) => patch({ account_ids: post.account_ids.includes(id) ? post.account_ids.filter((x) => x !== id) : [...post.account_ids, id] }, true);
  const chosen = accounts.filter((a) => post.account_ids?.includes(a.id));
  const pv = chosen.find((a) => a.id === preview) || chosen[0];
  const minLimit = Math.min(...chosen.map((a) => LIMITS[a.network] || 63206), 63206);
  const dup = async () => { try { await api.post(`/console/social/posts/${post.id}/duplicate`); toast.success("Post duplicated"); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async () => { if (!window.confirm("Delete this post?")) return; try { await api.delete(`/console/social/posts/${post.id}`); onClose(); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="fixed inset-0 !m-0 z-50 bg-[#F4F4F8] flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="social-post-editor">
      <header className="shrink-0 bg-white border-b border-[#EEEFEF] px-4 sm:px-6 py-3 flex items-center gap-2 sm:gap-3 flex-wrap">
        <h2 className="font-display text-lg font-semibold">{locked ? "Post" : "Edit post"}</h2>
        <StatusPill status={post.status} testid="social-editor-status" />
        <span className="hidden sm:inline text-xs text-[#9FA1A4]">{formatOf(post)}</span>
        {locked && <span className="order-last sm:order-none w-full sm:w-auto text-xs text-[#595D62] flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> {editable ? "Approved posts are locked" : "Locked while in review or approved"}</span>}
        <div className="ml-auto flex items-center gap-1">
          {editable && <button data-testid="social-editor-duplicate" onClick={dup} className="h-9 px-3 rounded-full text-sm text-[#595D62] hover:bg-[#F6F6FD] flex items-center gap-1.5"><Copy className="h-4 w-4" /><span className="hidden sm:inline"> Duplicate</span></button>}
          {!locked && <button data-testid="social-editor-delete" onClick={del} className="h-9 px-3 rounded-full text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-1.5"><Trash2 className="h-4 w-4" /><span className="hidden sm:inline"> Delete</span></button>}
          <button data-testid="social-editor-close" onClick={close} className="h-9 w-9 rounded-full flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><X className="h-5 w-5" /></button>
        </div>
      </header>
      <div className="flex-1 min-h-0 grid lg:grid-cols-[1fr_480px] overflow-auto">
        <div className="p-4 sm:p-6 space-y-5 lg:overflow-y-auto">
          <div>
            <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-2">Post to</p>
            <div className="flex flex-wrap gap-2">
              {accounts.map((a) => {
                const on = post.account_ids?.includes(a.id);
                return (
                  <button key={a.id} data-testid={`social-editor-account-${a.id}`} disabled={locked} onClick={() => toggle(a.id)} className={`flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 transition-colors ${on ? "border-[#723FFF] bg-[#F7F4FF]" : "border-[#EEEFEF] bg-white opacity-60 hover:opacity-100"}`}>
                    <AccountAvatar a={a} ws={ws} size={26} /><span className="text-xs text-[#06060C]">{a.name}</span>
                  </button>
                );
              })}
              {!accounts.length && <p className="text-xs text-[#9FA1A4]">No accounts yet. Add this client's pages under Accounts in SocialStudio.</p>}
            </div>
          </div>
          <PostTypeSwitch post={post} locked={locked} chosen={chosen} onChange={(v) => patch({ post_type: v }, true)} />
          <div className="rounded-2xl border border-[#EEEFEF] bg-white p-1">
            <Textarea data-testid="social-editor-caption" disabled={locked} rows={9} value={post.caption} onChange={(e) => patch({ caption: e.target.value })} placeholder="Write the caption…" className="border-0 shadow-none focus-visible:ring-0 text-[15px] leading-relaxed resize-y" />
            <div className="flex items-center justify-between px-3 py-1.5 border-t border-[#f3f3f6]">
              <span className="text-[11px] text-[#9FA1A4]">Saved automatically</span>
              <span data-testid="social-editor-count" className={`font-mono-data text-[11px] ${(post.caption || "").length > minLimit ? "text-rose-600" : "text-[#9FA1A4]"}`}>{(post.caption || "").length}{minLimit < 63206 ? ` / ${minLimit}` : ""}</span>
            </div>
          </div>
          <MediaStrip post={post} locked={locked} onChange={reload} />
          <div className="max-w-sm">
            <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-1.5">Publish date & time</p>
            <div className="flex items-center gap-2">
              <div className="flex-1"><DuePicker testid="social-editor-date" disabled={locked} placeholder="Pick a date" value={(post.scheduled_at || "").slice(0, 10) || null} onChange={(v) => patch({ scheduled_at: v ? `${v}T${(post.scheduled_at || "").slice(11, 16) || "09:00"}` : null }, true)} /></div>
              <select data-testid="social-editor-time" disabled={locked || !post.scheduled_at} value={(post.scheduled_at || "").slice(11, 16) || "09:00"} onChange={(e) => patch({ scheduled_at: `${post.scheduled_at.slice(0, 10)}T${e.target.value}` }, true)} className="h-9 rounded-md bg-white border border-[#EEEFEF] px-2 text-sm disabled:opacity-50">
                {TIMES.map((t) => <option key={t} value={t}>{new Date(`2000-01-01T${t}`).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</option>)}
              </select>
            </div>
          </div>
          <Comments post={post} onChange={reload} />
        </div>
        <aside className="border-t lg:border-t-0 lg:border-l border-[#EEEFEF] bg-white p-4 sm:p-5 lg:overflow-y-auto">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {chosen.map((a) => <button key={a.id} data-testid={`social-preview-tab-${a.id}`} onClick={() => setPreview(a.id)} className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs border ${pv?.id === a.id ? "border-[#06060C] bg-[#06060C] text-white" : "border-[#EEEFEF] text-[#595D62]"}`}>{React.createElement(NETS[a.network].icon, { className: "h-3.5 w-3.5" })}{a.name}</button>)}
          </div>
          <PostPreview post={post} account={pv} ws={ws} />
          <p className="text-[11px] text-[#9FA1A4] text-center mt-3">Previews are a close guide, not an exact copy of each network.</p>
        </aside>
      </div>
    </div>
  );
}
